import { html, render } from "lit-html";
import { createRef, ref } from "lit-html/directives/ref";
import { determineTranslation } from "../../utils/language.js";
import { toLocalFixed } from "../../utils/number.js";
import "../../website/cms-dialog.js";
import { CmsDialog } from "../../website/cms-dialog.js";

export class VippsBuyProductDialog extends HTMLElement {
    constructor(private onDecision: (decision: VippsBuyDecision) => void) {
        super();
        this.labels = determineTranslation(this.translations);
    }

    /* #region Public */
    public show(data: IVippsBuyProductData) {
        this.render(data);
        this.dialog.value?.showModal();
    }
    /* #endregion */

    /* #region Translations */
    private translations = {
        en: {
            title: "Pay with Vipps express checkout",
            youAlreadyHaveCart: "You already have products in",
            cart: "shopping cart",
            wouldYou: "Would you like to buy all products in cart including this product, or just this product?",
            buyAll: "Buy all in cart",
            onlyThis: "Buy only this product",
            totalPrice: "Total price:"
        },
        no: {
            title: "Betal med Vipps hurtigkasse",
            youAlreadyHaveCart: "Du har allerede varer i",
            cart: "handlekurven",
            wouldYou: "Vil du kjøpe alle varer i handlekurven inklusiv denne, eller kun denne varen?",
            buyAll: "Kjøp alle varer",
            onlyThis: "Kjøp kun denne varen",
            totalPrice: "Total pris:"
        }
    };

    private labels: { [key: string]:any };
    /* #endregion */

    /* #region Markup */
    private template = (data: IVippsBuyProductData) => html`
        <cms-dialog ${ref(this.dialog)} heading="${this.labels.title}">
            <div class="dialog-content">
                <p class="mb-0">${this.labels.youAlreadyHaveCart} <a href="${data.cartLink}">${this.labels.cart}</a></br>
                ${this.labels.wouldYou}</p>
                <div class="buttons">
                    <div>
                        <p><span class="fw-semibold">${this.labels.totalPrice}</span> <span class="price">${toLocalFixed(data.cartSum, 2)}</span></p>
                        <button class="btn btn-primary btn-buy-all" @click="${this.handleBuyAllClick.bind(this)}">${this.labels.buyAll}</button>
                    </div>
                    <div>
                        <p><span class="fw-semibold">${this.labels.totalPrice}</span> <span class="price">${toLocalFixed(data.productPrice, 2)}</span></p>
                        <button class="btn btn-primary btn-buy-only-this" @click="${this.handleBuyOnlyThisClick.bind(this)}">${this.labels.onlyThis}</button>
                    </div>
                </div>
            </div>
        </cms-dialog>
    `;
    /* #endregion */

    /* #region Event handlers */
    private handleBuyAllClick(e: MouseEvent) {
        e.preventDefault();

        if (this.onDecision) {
            this.onDecision(VippsBuyDecision.WholeCart);
        }

        this.dialog.value?.close();
    }

    private handleBuyOnlyThisClick(e: MouseEvent) {
        e.preventDefault();

        if (this.onDecision) {
            this.onDecision(VippsBuyDecision.OnlyThisProduct);
        }

        this.dialog.value?.close();
    }

    /* #endregion */

    /* #region Private */

    private dialog = createRef<CmsDialog>();

    private render(data: IVippsBuyProductData) {
        render(this.template(data), this);
    }

    public setOnDecision(onDecision) {
        this.onDecision = onDecision;
    }
    /* #endregion */

    connectedCallback() { }
}

export interface IVippsBuyProductData {
    cartLink: string;
    cartCount: number;
    cartSum: number;
    productPrice: number;
}

export enum VippsBuyDecision {
    Cancel = "cancel",
    WholeCart = "whole-cart",
    OnlyThisProduct = "only-product"
}

customElements.define("vipps-buy-product-dialog", VippsBuyProductDialog);